import { useEffect, useRef, type ReactNode } from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { PageLoader } from '@/components/auth/PageLoader'
import { buildLoginRedirect } from '@/utils/authRedirect'

interface RequireAuthProps {
  children: ReactNode
}

export function RequireAuth({ children }: RequireAuthProps) {
  const status = useAuthStore((s) => s.status)
  const loadMe = useAuthStore((s) => s.loadMe)
  const location = useLocation()

  useEffect(() => {
    if (status === 'idle') void loadMe()
  }, [status, loadMe])

  if (status === 'idle' || status === 'loading') return <PageLoader />
  if (status === 'authed') return <>{children}</>

  return <Navigate to={buildLoginRedirect(`${location.pathname}${location.search}`)} replace />
}

interface RequireWorkspaceProps {
  children: ReactNode
}

export function RequireWorkspace({ children }: RequireWorkspaceProps) {
  const memberships = useAuthStore((s) => s.memberships)
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>()
  if (memberships.length === 0) {
    return <Navigate to="/workspace/create" replace />
  }
  if (workspaceSlug && !memberships.some((membership) => membership.workspace_slug === workspaceSlug)) {
    return <Navigate to="/workspaces" replace />
  }
  return <>{children}</>
}

interface RequireVerifiedEmailProps {
  children: ReactNode
}

/**
 * Keeps an unconfirmed account out of the product.
 *
 * Mirrors the API, which answers 403 EMAIL_NOT_VERIFIED on the same routes — the
 * guard exists so the user sees the confirmation screen instead of a wall of
 * failed requests. Only an explicit `false` redirects: a payload without the flag
 * (older cached response) must never lock anyone out.
 */
export function RequireVerifiedEmail({ children }: RequireVerifiedEmailProps) {
  const user = useAuthStore((s) => s.user)
  if (user?.email_verified === false) return <Navigate to="/verify-email" replace />
  return <>{children}</>
}

export function RequireStaff({ children }: RequireAuthProps) {
  const user = useAuthStore((state) => state.user)
  if (!user?.is_staff) return <Navigate to="/workspaces" replace />
  return <>{children}</>
}

interface RequireGuestProps {
  children: ReactNode
}

export function RequireGuest({ children }: RequireGuestProps) {
  const status = useAuthStore((s) => s.status)
  const loadMe = useAuthStore((s) => s.loadMe)
  // Once the session check has answered "guest", the form stays mounted for the
  // rest of the visit. Submitting flips the store to 'loading', and swapping in
  // the loader here would unmount the page and wipe its local state — including
  // the "senha incorreta" message the failed attempt just produced.
  const resolvedAsGuest = useRef(false)

  useEffect(() => {
    if (status === 'idle') void loadMe()
  }, [status, loadMe])

  if (status === 'guest' || status === 'expired') resolvedAsGuest.current = true

  if (status === 'authed') return <Navigate to="/workspaces" replace />
  if (!resolvedAsGuest.current && (status === 'idle' || status === 'loading')) return <PageLoader />
  return <>{children}</>
}
